import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../components/Modal/Modal';
import {
  getSystemSettings,
  updateSystemSettings,
  getIntegrationConfigs,
  upsertIntegrationConfig,
  deleteIntegrationConfig,
} from '../../../api/services/adminService';
import './SystemSettings.css';

const PROVIDER_PRESETS = {
  MPESA: ['consumerKey', 'consumerSecret', 'shortcode', 'passkey', 'accountReference'],
  PAYSTACK: ['secretKey', 'publicKey', 'subaccountCode'],
  FACEBOOK_PAGE: ['pageId', 'pageAccessToken', 'graphApiVersion'],
};

const getEnabledPresetKeys = (category, provider) => {
  if (category === 'FACEBOOK') return PROVIDER_PRESETS.FACEBOOK_PAGE;
  return PROVIDER_PRESETS[provider] || [];
};

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '—';
  }
};

const SystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [toggleMsg, setToggleMsg] = useState(null);

  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(true);
  const [configsError, setConfigsError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category: 'PAYMENT',
    provider: 'MPESA',
    displayName: '',
    enabled: false,
    credentialPairs: [{ key: '', value: '' }],
  });
  const [formError, setFormError] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await getSystemSettings();
      setSettings(res.data || null);
    } catch (err) {
      setSettingsError(err.message || 'Failed to load system settings');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const loadConfigs = useCallback(async () => {
    setConfigsLoading(true);
    setConfigsError(null);
    try {
      const res = await getIntegrationConfigs();
      setConfigs(res.data || []);
    } catch (err) {
      setConfigsError(err.message || 'Failed to load integration configs');
    } finally {
      setConfigsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadConfigs();
  }, [loadSettings, loadConfigs]);

  const handleToggle = async (field) => {
    if (!settings) return;
    const nextValue = !settings[field];
    setSettingsSaving(true);
    setToggleMsg(null);
    try {
      const res = await updateSystemSettings({ [field]: nextValue });
      setSettings(res.data || { ...settings, [field]: nextValue });
      setToggleMsg('Settings updated');
    } catch (err) {
      setToggleMsg(err.message || 'Failed to update settings');
    } finally {
      setSettingsSaving(false);
      setTimeout(() => setToggleMsg(null), 3000);
    }
  };

  const openAddModal = () => {
    setEditing(null);
    setForm({
      category: 'PAYMENT',
      provider: 'MPESA',
      displayName: '',
      enabled: false,
      credentialPairs: getEnabledPresetKeys('PAYMENT', 'MPESA').map((k) => ({ key: k, value: '' })),
    });
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = (config) => {
    setEditing(config);
    const presetKeys = getEnabledPresetKeys(config.category, config.provider);
    const existingKeys = config.credentials ? Object.keys(config.credentials) : [];
    const keysToUse = existingKeys.length > 0 ? existingKeys : presetKeys;
    const pairs = keysToUse.map((k) => ({ key: k, value: '' }));
    if (pairs.length === 0) pairs.push({ key: '', value: '' });

    setForm({
      category: config.category,
      provider: config.provider,
      displayName: config.displayName || '',
      enabled: !!config.enabled,
      credentialPairs: pairs,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProviderChange = (value) => {
    const presetKeys = getEnabledPresetKeys(form.category, value);
    setForm((prev) => ({
      ...prev,
      provider: value,
      credentialPairs: presetKeys.map((k) => ({ key: k, value: '' })),
    }));
  };

  const handleCategoryChange = (value) => {
    const defaultProvider = value === 'FACEBOOK' ? 'FACEBOOK_PAGE' : 'MPESA';
    const presetKeys = getEnabledPresetKeys(value, defaultProvider);
    setForm((prev) => ({
      ...prev,
      category: value,
      provider: defaultProvider,
      credentialPairs: presetKeys.map((k) => ({ key: k, value: '' })),
    }));
  };

  const updateCredentialPair = (index, field, value) => {
    setForm((prev) => {
      const pairs = [...prev.credentialPairs];
      pairs[index] = { ...pairs[index], [field]: value };
      return { ...prev, credentialPairs: pairs };
    });
  };

  const addCredentialPair = () => {
    setForm((prev) => ({
      ...prev,
      credentialPairs: [...prev.credentialPairs, { key: '', value: '' }],
    }));
  };

  const removeCredentialPair = (index) => {
    setForm((prev) => ({
      ...prev,
      credentialPairs: prev.credentialPairs.filter((_, i) => i !== index),
    }));
  };

  const handleSaveConfig = async () => {
    setFormError(null);

    if (!form.provider || !form.provider.trim()) {
      setFormError('Provider is required');
      return;
    }
    if (!form.category) {
      setFormError('Category is required');
      return;
    }

    const credentials = {};
    let hasCreds = false;
    for (const pair of form.credentialPairs) {
      const key = (pair.key || '').trim();
      if (!key) continue;
      const value = (pair.value || '').trim();
      credentials[key] = value;
      hasCreds = true;
    }

    const dto = {
      category: form.category,
      provider: form.provider.trim().toUpperCase(),
      displayName: form.displayName ? form.displayName.trim() : null,
      enabled: !!form.enabled,
      credentials: hasCreds ? credentials : null,
    };

    setFormSaving(true);
    try {
      await upsertIntegrationConfig(dto);
      setModalOpen(false);
      await loadConfigs();
    } catch (err) {
      setFormError(err.message || 'Failed to save integration config');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Delete this integration config? This cannot be undone.')) return;
    try {
      await deleteIntegrationConfig(configId);
      await loadConfigs();
    } catch (err) {
      setConfigsError(err.message || 'Failed to delete integration config');
    }
  };

  const handleToggleConfigEnabled = async (config) => {
    try {
      await upsertIntegrationConfig({
        category: config.category,
        provider: config.provider,
        displayName: config.displayName,
        enabled: !config.enabled,
      });
      await loadConfigs();
    } catch (err) {
      setConfigsError(err.message || 'Failed to toggle integration config');
    }
  };

  const renderCredentials = (config) => {
    const creds = config.credentials;
    if (!creds || Object.keys(creds).length === 0) {
      return <div className="integration-credential-empty">No credentials stored</div>;
    }
    return Object.entries(creds).map(([k, v]) => (
      <div className="integration-credential-row" key={k}>
        <span className="integration-credential-key">{k}</span>
        <span className="integration-credential-value">{v === '' ? '(empty)' : String(v)}</span>
      </div>
    ));
  };

  return (
    <div className="system-settings-container">
      <h1 className="page-title">System Settings</h1>

      <h2 className="settings-section-title">Platform Toggles</h2>
      <p className="settings-section-subtitle">Control global platform behaviour.</p>

      {settingsLoading ? (
        <div className="settings-loading">Loading settings…</div>
      ) : settingsError ? (
        <div className="settings-error">{settingsError}</div>
      ) : settings ? (
        <div className="settings-list">
          <div className="setting-card">
            <div className="setting-info">
              <h3>Subscription Mode</h3>
              <p>Require active subscription for landlords / hosts to access dashboard features.</p>
            </div>
            <button
              type="button"
              className={`toggle-btn ${settings.subscriptionEnabled ? 'active' : ''}`}
              onClick={() => handleToggle('subscriptionEnabled')}
              disabled={settingsSaving}
            >
              {settings.subscriptionEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <div className="setting-card">
            <div className="setting-info">
              <h3>Facebook Auto-Post</h3>
              <p>Automatically post new property listings to the connected Facebook page.</p>
            </div>
            <button
              type="button"
              className={`toggle-btn ${settings.facebookAutoPostEnabled ? 'active' : ''}`}
              onClick={() => handleToggle('facebookAutoPostEnabled')}
              disabled={settingsSaving}
            >
              {settings.facebookAutoPostEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>
      ) : null}

      {toggleMsg && <p className="settings-section-subtitle">{toggleMsg}</p>}

      <h2 className="settings-section-title">Integration Configs</h2>
      <p className="settings-section-subtitle">
        Manage third-party payment providers and Facebook page credentials. Secret values are masked after saving —
        re-enter them when updating.
      </p>

      <button type="button" className="btn btn-primary integration-add-btn" onClick={openAddModal}>
        + Add Integration
      </button>

      {configsLoading ? (
        <div className="settings-loading">Loading integrations…</div>
      ) : configsError ? (
        <div className="settings-error">{configsError}</div>
      ) : configs.length === 0 ? (
        <div className="settings-empty">No integration configs yet. Click “Add Integration” to configure one.</div>
      ) : (
        <div className="integration-list">
          {configs.map((config) => (
            <div className="integration-card" key={config.id}>
              <div className="integration-card-head">
                <div>
                  <h3 className="integration-card-title">{config.displayName || config.provider}</h3>
                  <p className="integration-card-meta">Updated {formatTimestamp(config.updatedAt)}</p>
                </div>
                <div>
                  <span className={`integration-badge category-${config.category.toLowerCase()}`}>
                    {config.category}
                  </span>
                  <span className={`integration-badge status-${config.enabled ? 'on' : 'off'}`}>
                    {config.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="integration-credentials">{renderCredentials(config)}</div>

              <div className="integration-card-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleToggleConfigEnabled(config)}
                >
                  {config.enabled ? 'Disable' : 'Enable'}
                </button>
                <button type="button" className="btn btn-primary" onClick={() => openEditModal(config)}>
                  Edit
                </button>
                <button type="button" className="btn btn-danger" onClick={() => handleDeleteConfig(config.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <Modal onClose={() => setModalOpen(false)} className="credential-modal" ariaLabel="Integration config">
          <h2>{editing ? 'Edit Integration' : 'Add Integration'}</h2>
          <p className="modal-subtitle">
            Configure credentials for a payment provider or Facebook page. Secret values are stored securely and masked
            in responses.
          </p>

          {formError && <div className="credential-form-error">{formError}</div>}

          <div className="credential-form-group">
            <label htmlFor="ig-category">Category</label>
            <select
              id="ig-category"
              value={form.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              disabled={!!editing}
            >
              <option value="PAYMENT">PAYMENT</option>
              <option value="FACEBOOK">FACEBOOK</option>
            </select>
          </div>

          <div className="credential-form-group">
            <label htmlFor="ig-provider">Provider</label>
            {editing ? (
              <input id="ig-provider" type="text" value={form.provider} readOnly />
            ) : form.category === 'FACEBOOK' ? (
              <select id="ig-provider" value={form.provider} onChange={(e) => handleProviderChange(e.target.value)}>
                <option value="FACEBOOK_PAGE">FACEBOOK_PAGE</option>
              </select>
            ) : (
              <select id="ig-provider" value={form.provider} onChange={(e) => handleProviderChange(e.target.value)}>
                <option value="MPESA">MPESA</option>
                <option value="PAYSTACK">PAYSTACK</option>
              </select>
            )}
          </div>

          <div className="credential-form-group">
            <label htmlFor="ig-display-name">Display Name (optional)</label>
            <input
              id="ig-display-name"
              type="text"
              value={form.displayName}
              onChange={(e) => handleFormChange('displayName', e.target.value)}
              placeholder="e.g. M-Pesa Production"
            />
          </div>

          <div className="credential-toggle-row">
            <label htmlFor="ig-enabled">Enabled</label>
            <input
              id="ig-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => handleFormChange('enabled', e.target.checked)}
            />
          </div>

          <div className="credential-form-group">
            <label>Credentials</label>
            <div className="credential-pairs">
              {form.credentialPairs.map((pair, index) => (
                <div className="credential-pair-row" key={index}>
                  <input
                    type="text"
                    placeholder="Key (e.g. consumerKey)"
                    value={pair.key}
                    onChange={(e) => updateCredentialPair(index, 'key', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Value (re-enter to update)"
                    value={pair.value}
                    onChange={(e) => updateCredentialPair(index, 'value', e.target.value)}
                  />
                  <button
                    type="button"
                    className="credential-pair-remove"
                    onClick={() => removeCredentialPair(index)}
                    aria-label="Remove credential"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="credential-add-pair" onClick={addCredentialPair}>
              + Add credential field
            </button>
          </div>

          <div className="credential-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)} disabled={formSaving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSaveConfig} disabled={formSaving}>
              {formSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SystemSettings;
